import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useClubs } from "src/features/club/ClubProvider";
import { CSVLink } from "react-csv";
import * as toastr from "toastr";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

// Soft UI Dashboard PRO React components
import SoftBox from "src/components/SoftBox";
import SoftTypography from "src/components/SoftTypography";
import SoftButton from "src/components/SoftButton";
import SoftDateRangePicker from "src/components/SoftDateRangePicker";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "src/containers/LayoutContainers/DashboardLayout";
import DashboardNavbar from "src/containers/DashboardNavbar";
import Footer from "src/components/Footer";
import DataTable from "src/components/Tables/DataTable";

// Data
import { getTableColumns } from "./tableColumns";
import { query, orderBy, where } from "firebase/firestore";
import useRealtimeCollectionData from "src/features/firebase/firestore/useRealtimeCollectionData";
import { Modal } from "@mui/material";
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfDay,
  endOfDay,
} from "date-fns";
import { getClubPayments } from "src/features/club/collections";
import { WithPermissions } from "src/components/WithPermissions/WithPermissions";
import { systemPermissions } from "src/interfaces/roles/role.interface";
import WithPermissionWrapper from "src/components/WithPermissions/WithPermissionWrapper";
import { AddPaymentModal } from "src/modals/AddPaymentModal";
import Swal from "sweetalert2";
import { syncPayment } from "src/services/quickbooks.api";
import { useUser } from "src/features/user/UserProvider";

function PaymentList() {
  const navigate = useNavigate();
  const {
    selectedClubId,
    selectedClub,
    selectedLocation,
    selectedLocationId,
    clubUsers,
  } = useClubs();
  const { userId } = useUser();
  const [menu, setMenu] = useState(null);
  const [paymentsRows, setPaymentsRows] = useState([]);
  const [openAddNewPayment, setOpenAddNewPayment] = useState(false);
  const [bulkMenu, setBulkMenu] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [isSyncingPayments, setIsSyncingPayments] = useState(false);
  const [tableRows, setTableRows] = useState([]);
  const [csvData, setCsvData] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [dateRange, setDateRange] = useState([
    startOfMonth(subMonths(new Date(), 1)),
    endOfMonth(subMonths(new Date(), 1)),
  ]);

  const updateDateRange = (dates) => {
    dates[0] = startOfDay(dates[0]);
    dates[1] = endOfDay(dates[1]);

    setDateRange(dates);
  };

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = (filter) => {
    setMenu(null);
    if (filter === "paid") {
      setIsFiltered(true);
      const rows = [...paymentsRows]
        .filter((row) => {
          const amountPaid =
            row?.payments?.reduce((acc, payment) => {
              if (payment?.status === "succeeded")
                return acc + (payment?.amount ?? 0) / 100;
              else return acc;
            }, 0) ?? 0;

          const totalCost = Math.round(row.totalCost * 100) / 100 || 0;

          return row.paid || amountPaid >= totalCost - (row.fuelCost ?? 0);
        })
        .sort((a, b) => {
          return b.createdAt.toDate() - a.createdAt.toDate();
        });

      setPaymentsRows(rows);
      return;
    }
    if (filter === "unpaid") {
      setIsFiltered(true);
      const rows = [...allPayments]
        .filter((row) => {
          const amountPaid =
            row?.payments?.reduce((acc, payment) => {
              if (payment?.status === "succeeded")
                return acc + (payment?.amount ?? 0) / 100;
              else return acc;
            }, 0) ?? 0;

          const totalCost = Math.round(row.totalCost * 100) / 100 || 0;

          return totalCost > 0 && amountPaid < totalCost - (row.fuelCost ?? 0);
        })
        .sort((a, b) => {
          return b.createdAt.toDate() - a.createdAt.toDate();
        });

      setPaymentsRows(rows);
      return;
    }
    if (filter && filter !== "all") {
      setIsFiltered(true);
      const rows = [...allPayments]
        .filter((row) => row.type === filter)
        .sort((a, b) => {
          return b.createdAt.toDate() - a.createdAt.toDate();
        });
      setPaymentsRows(rows);
      return;
    } else if (filter === "all") {
      const rows = [...allPayments].sort((a, b) => {
        return b.createdAt.toDate() - a.createdAt.toDate();
      });
      setPaymentsRows(rows);
      setIsFiltered(false);
    }
  };

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={Boolean(menu)}
      onClose={() => closeMenu()}
      keepMounted
    >
      <MenuItem onClick={() => closeMenu("paid")}>Status: Paid</MenuItem>
      <MenuItem onClick={() => closeMenu("processing")}>
        Status: Processing
      </MenuItem>
      <MenuItem onClick={() => closeMenu("unpaid")}>Status: Unpaid</MenuItem>
      <Divider
        sx={{
          margin: "0.5rem 0",
        }}
      />
      <MenuItem onClick={() => closeMenu("all")}>
        <SoftTypography variant="button" color="error" fontWeight="regular">
          Remove Filter
        </SoftTypography>
      </MenuItem>
    </Menu>
  );

  const renderBulkMenu = (
    <Menu
      anchorEl={bulkMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={Boolean(bulkMenu)}
      onClose={() => closeBulkMenu()}
      keepMounted
    >
      <MenuItem onClick={() => closeBulkMenu("qboSync")}>
        Sync to Quickbooks
      </MenuItem>
    </Menu>
  );

  const clubPaymentsCollectionRef = query(
    getClubPayments(selectedClubId),
    where("createdAt", ">=", dateRange[0]),
    where("locationId", "==", selectedLocationId),
    orderBy("createdAt", "desc")
  );

  const { data: allPayments, isDataLoaded: isPaymentsLoaded } =
    useRealtimeCollectionData(clubPaymentsCollectionRef, true);

  useMemo(() => {
    if (!allPayments && !isPaymentsLoaded) {
      return;
    }

    const rows = [
      ...allPayments.map((value) => ({
        ...value,
        member: clubUsers.get(value.userId),
      })),
    ].sort((a, b) => {
      return b.date.toDate() - a.date.toDate();
    });
    setPaymentsRows(rows);
  }, [allPayments, isPaymentsLoaded, selectedLocationId, dateRange]);

  const updateCsvData = (rows) => {
    const csvDataFromRows = rows.map((row) => ({
      ID: row.id,
      Date: row.createdAt
        ? format(row.createdAt.toDate(), "yyyy/MM/dd hh:mm:ss")
        : "---",
      Member: row.member?.displayName || row.pilot?.displayName || "---",
      "Transaction Type": row.type,
      "Total Amount": row?.totalCost || row.totalAmount,
      "Amount Paid":
        Math.round(
          (row.payments?.length > 0
            ? row.payments.reduce(
                (acc, payment) =>
                  payment?.status === "succeeded"
                    ? acc + payment.amount / 100
                    : acc,
                0
              )
            : 0) * 100
        ) / 100,
      "Service Fees": Math.round((row.fees?.applicationFee || 0) * 100) / 100,
      Revenue: Math.round((row.fees?.clubRevenue || 0) * 100) / 100,
    }));

    setCsvData(csvDataFromRows);
  };

  useMemo(() => {
    updateCsvData(tableRows);
  }, [tableRows]);

  const onRowSelected = (row) => {
    navigate({
      pathname: `/billing/payments/${row.id}`,
    });
  };

  const openBulkMenu = (event) => setBulkMenu(event.currentTarget);
  const closeBulkMenu = (action) => {
    setBulkMenu(null);
    if (action === "qboSync") {
      console.log(
        "Syncing to Quickbooks for selected payments:",
        selectedPayments
      );
      if (isSyncingPayments) {
        toastr.error(
          "You are already syncing payments to QuickBooks. Please wait until the current sync is complete.",
          "Sync in Progress"
        );
        return;
      }
      const newSwal = Swal.mixin({
        customClass: {
          cancelButton: "button button-error",
        },
        buttonsStyling: false,
      });

      newSwal
        .fire({
          title: "Are you sure?",
          text: "This will create these payments in your QuickBooks account! (If they do not exist already)",
          showCancelButton: true,
          confirmButtonText: "Yes!",
          cancelButtonText: "No",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            setIsSyncingPayments(true);

            Promise.all(
              selectedPayments.map((paymentId) => {
                return syncPayment(paymentId, selectedClubId, userId);
              })
            )
              .then((result) => {
                console.log("Sync result:", result);
                if (!result.some((res) => !res.success)) {
                  toastr.success(
                    `The payments were created in QuickBooks.`,
                    "Success"
                  );
                } else {
                  toastr.error(
                    `There was an error creating the payments in QuickBooks`,
                    "Whoops!"
                  );
                }
              })
              .finally(() => {
                setIsSyncingPayments(false);
                setSelectedPayments([]);
              });
          }
        });
      return;
    }
  };

  const addNewPayment = () => {
    setOpenAddNewPayment(true);
  };

  const handleNewPaymentClose = () => {
    setOpenAddNewPayment(false);
  };

  const tableColumns = useMemo(
    () => getTableColumns(selectedPayments, setSelectedPayments),
    [selectedPayments]
  );
  const tableData = useMemo(() => paymentsRows, [paymentsRows]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <SoftBox>
            <WithPermissions permissions={systemPermissions.ADD_INVOICES}>
              <SoftButton
                variant="gradient"
                color="info"
                onClick={addNewPayment}
              >
                New Payment
              </SoftButton>
            </WithPermissions>
          </SoftBox>
          <SoftBox display="flex">
            {selectedPayments.length > 0 && (
              <SoftButton
                variant="contained"
                color="error"
                onClick={openBulkMenu}
                style={{ marginRight: "15px" }}
              >
                Bulk Actions&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </SoftButton>
            )}
            {renderBulkMenu}
            <SoftDateRangePicker
              value={dateRange}
              onChange={(newDates) => updateDateRange(newDates)}
            />
            <SoftBox mx={1}>
              <Divider orientation="vertical" />
            </SoftBox>
            <SoftButton
              variant={menu || isFiltered ? "contained" : "outlined"}
              color="dark"
              onClick={openMenu}
            >
              filters&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </SoftButton>
            {renderMenu}
            {csvData && (
              <SoftBox ml={1}>
                <SoftBox>
                  <CSVLink
                    data={csvData}
                    filename={`${selectedClub.name}-${
                      selectedLocation?.icao
                    }-billing_invoices-${format(new Date(), "MM/dd/yyyy")}.csv`}
                    target="_blank"
                  >
                    <SoftButton variant="outlined" color="dark">
                      <Icon>description</Icon>
                      &nbsp;export csv
                    </SoftButton>
                  </CSVLink>
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>
        <Card>
          {paymentsRows && paymentsRows.length > 0 && (
            <DataTable
              onRowSelect={onRowSelected}
              entriesPerPage={{ defaultValue: 50, entries: [10, 20, 50, 100] }}
              table={{
                columns: tableColumns,
                rows: tableData,
              }}
              onRowsChanged={(rows) => {
                setTableRows(rows.map((row) => row.original));
              }}
              canSearch
            />
          )}
        </Card>
      </SoftBox>
      <Footer />
      <Modal
        open={openAddNewPayment}
        onClose={handleNewPaymentClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          backdropFilter: "blur(2px)",
        }}
      >
        <SoftBox>
          <AddPaymentModal handleClose={handleNewPaymentClose} />
        </SoftBox>
      </Modal>
    </DashboardLayout>
  );
}

export default WithPermissionWrapper(
  systemPermissions.VIEW_PAYMENTS,
  PaymentList
);
